/**
 * ASK MORE — the host's MIME (Make It More Epic) panel, README "Operations tabs · ASK MORE".
 *
 * Live round as a black card: ROUND n · LIVE, the cyan shot clock, `+$15.00 each`, the
 * host's reason in quotes, the 4-up tally (IN green / OUT / SILENT amber / RAISED
 * green), NUDGE THE SILENT n (POST /mime/rounds/{id}/nudge, once per 10 minutes) and END
 * THE ROUND EARLY (the existing cancel — pending requests expire, accepted commitments
 * stay). WHO ANSWERED rows under it, the START ANOTHER ROUND form inline, and the
 * COMPLETED ROUNDS summary. Polls every 5s only while mounted with a round live.
 *
 * Props are the ones EventOperationPage always passed (`eventId`, `mimeService`,
 * `acceptedCount`, `eventStatus`) plus `mobile` and `onSummaryChange`. Built on the host
 * kit (./host/kit); the status pills are the surface Pill restyled borderless.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDialog } from '../context';
import { Pill, Countdown } from './surface';
import LoadingSpinner from './LoadingSpinner';
import { money, agoLower } from '../utils/surfaceFormat';
import { Card, SectionHead, Btn, Field, AffixInput, Select, Textarea, ToggleRow, Ava, RowText, Empty, Help, ErrorLine } from './host/kit';

const ROUND_STATUSES = ['funding', 'greenlit', 'selling'];
const CLOCKS = [[5, '5 MIN'], [15, '15 MIN'], [30, '30 MIN'], [60, '60 MIN'], [120, '2 HOURS'], [360, '6 HOURS'], [720, '12 HOURS'], [1440, '24 HOURS']];
const NUDGE_COOLDOWN_MS = 10 * 60 * 1000;

const GREEN = '#22C55E';
const MONEY_GREEN = '#16A34A';
const AMBER = '#FCAA6D';
const FAINT = '#9CA3AF';

/** Borderless status pills: { bg, fg } by label. */
const PILL_LOOK = {
  IN: { bg: '#22C55E', fg: '#fff' },
  OUT: { bg: '#F2F2F2', fg: '#6B7280' },
  SILENT: { bg: '#FFF8EA', fg: '#B45309' },
  EXPIRED: { bg: '#FEE2E2', fg: '#B91C1C' },
  DROPPED: { bg: '#FEE2E2', fg: '#B91C1C' },
  REJECTED: { bg: '#FEE2E2', fg: '#B91C1C' },
};

function StatusPill({ label }) {
  const look = PILL_LOOK[label] || PILL_LOOK.OUT;
  return <Pill size="sm" style={{ borderRadius: 999, borderColor: 'transparent', background: look.bg, color: look.fg }}>{label}</Pill>;
}

/** `41m ago` / `2h ago` / `yesterday` / `mar 4` — the kit's relative age, lower-case, with "ago" on the short forms. */
const rel = (iso) => { const a = agoLower(iso); return /^\d+[mh]$/.test(a) ? `${a} ago` : a; };

function requestRow(req, round) {
  const s = String(req.status || '').toLowerCase();
  if (s === 'accepted') {
    const cents = req.committedAmountCents ?? round?.additionalCommitmentCents ?? 0;
    return { when: req.respondedAt ? `accepted ${rel(req.respondedAt)}` : 'accepted', pill: 'IN', amount: cents > 0 ? money(cents, { signed: true }) : '—' };
  }
  if (s === 'declined') return { when: req.respondedAt ? `declined ${rel(req.respondedAt)}` : 'declined', pill: 'OUT', amount: '—' };
  if (s === 'expired') return { when: 'never answered', pill: 'EXPIRED', amount: '—' };
  if (s === 'removed') return { when: 'dropped for not answering', pill: 'DROPPED', amount: '—' };
  return { when: 'no answer yet', pill: 'SILENT', amount: '—' };
}

/** Card head: a 13px title on the left and a caps word on the right. */
function Head({ title, right }) {
  return (
    <div className="hev-sec" style={{ alignItems: 'center' }}>
      <span className="hev-sec-title">{title}</span>
      {right}
    </div>
  );
}

export default function MimePanel({ eventId, mimeService, acceptedCount = 0, eventStatus = 'draft', mobile = false, onSummaryChange }) {
  const { showAlert, showConfirm } = useDialog();
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [nudge, setNudge] = useState({ busy: false, at: 0, tooSoon: false });
  const [ending, setEnding] = useState(false);
  // START ANOTHER ROUND
  const [amount, setAmount] = useState('');
  const [clock, setClock] = useState(60);
  const [reason, setReason] = useState('');
  const [dropSilent, setDropSilent] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const activeRound = summary?.activeRound || null;

  const fetchSummary = useCallback(async () => {
    if (!eventId || !mimeService) return;
    try {
      const result = await mimeService.getEventSummary(eventId);
      if (result.success) {
        setSummary(result.data);
        onSummaryChange?.(result.data);
        if (result.data?.activeRound) {
          const rr = await mimeService.getRoundRequests(result.data.activeRound.id);
          if (rr.success) setRequests(rr.data || []);
        } else {
          setRequests([]);
        }
      }
    } catch (err) {
      console.error('Failed to fetch MIME summary:', err);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, mimeService]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  // Poll only while a round is live and this panel is mounted.
  useEffect(() => {
    if (!activeRound) return undefined;
    const id = setInterval(fetchSummary, 5000);
    return () => clearInterval(id);
  }, [activeRound, fetchSummary]);

  // Nudge cooldown clock.
  useEffect(() => {
    if (!nudge.tooSoon) return undefined;
    const id = setTimeout(() => setNudge((n) => ({ ...n, tooSoon: false })), Math.max(1000, NUDGE_COOLDOWN_MS - (Date.now() - nudge.at)));
    return () => clearTimeout(id);
  }, [nudge.tooSoon, nudge.at]);

  const raisedCents = useMemo(() => {
    if (!activeRound) return 0;
    const fromRows = requests.filter((r) => String(r.status).toLowerCase() === 'accepted').reduce((s, r) => s + (r.committedAmountCents ?? activeRound.additionalCommitmentCents ?? 0), 0);
    return fromRows || (activeRound.acceptedCount || 0) * (activeRound.additionalCommitmentCents || 0);
  }, [requests, activeRound]);

  const handleNudge = async () => {
    if (!activeRound || nudge.busy) return;
    setNudge((n) => ({ ...n, busy: true }));
    const r = await mimeService.nudgeRound(activeRound.id);
    if (r.success) {
      // Either way the button rests for 10 minutes: a fresh nudge starts the cooldown,
      // a `tooSoon` answer means one is already running.
      setNudge({ busy: false, at: Date.now(), tooSoon: true });
      if (r.data?.tooSoon) showAlert({ title: 'NOT YET', message: 'You nudged them less than 10 minutes ago. Give them a moment.', type: 'info' });
    } else {
      setNudge((n) => ({ ...n, busy: false }));
      showAlert({ title: 'ERROR', message: r.error || 'Could not nudge', type: 'error' });
    }
  };

  const handleEndEarly = async () => {
    if (!activeRound) return;
    const confirmed = await showConfirm({
      title: 'CANCEL MIME ROUND',
      message: 'Are you sure you want to cancel this MIME round? Pending requests will be expired.',
      confirmLabel: 'CANCEL ROUND',
    });
    if (!confirmed) return;
    setEnding(true);
    try {
      const result = await mimeService.cancelRound(activeRound.id);
      if (result.success) fetchSummary();
      else showAlert({ title: 'ERROR', message: result.error || 'Failed to cancel MIME round', type: 'error' });
    } catch {
      showAlert({ title: 'ERROR', message: 'An unexpected error occurred', type: 'error' });
    } finally {
      setEnding(false);
    }
  };

  const amountCents = Math.round((parseFloat(amount || '0') || 0) * 100);
  const amountValid = amountCents >= 0 && amountCents <= 100000;
  const nextRound = (summary?.totalRounds || 0) + 1;
  const blocked = activeRound
    ? `FINISH ROUND ${activeRound.roundNumber} FIRST`
    : acceptedCount === 0
      ? 'ACCEPT GUESTS FIRST'
      : !ROUND_STATUSES.includes(eventStatus)
        ? (eventStatus === 'draft' ? 'PUBLISH THE EVENT FIRST' : 'THIS EVENT IS CLOSED')
        : null;

  const handleCreate = async () => {
    if (blocked || creating) return;
    setCreateError(null);
    if (!amountValid) { setCreateError('EXTRA PER GUEST MUST BE BETWEEN $0 AND $1,000.'); return; }
    const confirmed = await showConfirm({
      title: 'SEND MIME ROUND',
      message: `${amountCents > 0 ? `Ask every accepted guest for ${money(amountCents)} more` : 'Send an update every accepted guest has to answer'}, with ${CLOCKS.find(([m]) => m === clock)?.[1].toLowerCase() || `${clock} min`} on the clock${dropSilent ? '. Anyone who does not answer is dropped and refunded' : ''}. Guests who withdraw during the window get a full refund, no flake fee.`,
      confirmLabel: 'SEND IT',
      variant: 'primary',
    });
    if (!confirmed) return;
    setCreating(true);
    try {
      const r = await mimeService.createRound(eventId, {
        additionalCommitmentCents: amountCents,
        message: reason.trim() || null,
        shotClockMinutes: clock,
        removeNonResponders: dropSilent,
      });
      if (r.success) {
        setAmount(''); setReason(''); setClock(60); setDropSilent(false);
        await fetchSummary();
      } else {
        setCreateError((r.error || 'Could not start the round').toString().toUpperCase());
      }
    } finally {
      setCreating(false);
    }
  };

  if (isLoading) {
    return <Card><LoadingSpinner label="Loading MIME..." /></Card>;
  }

  const completed = summary?.completedRounds || [];
  const silent = activeRound?.pendingCount || 0;
  const nudgeLabel = nudge.busy ? 'NUDGING…' : nudge.tooSoon ? 'NUDGED — TRY AGAIN IN 10 MIN' : `NUDGE THE SILENT ${silent}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }} data-testid="mime-panel">
      {activeRound ? (
        <Card black gap="14" testId="mime-live-round" style={{ borderRadius: 28 }}>
          <div className="hev-rail-head">
            <span className="hev-rail-title">ROUND {activeRound.roundNumber} · LIVE</span>
            <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.1em', color: '#00FFFF', whiteSpace: 'nowrap' }} data-testid="mime-clock">
              {/* eslint-disable-next-line react/style-prop-object -- Countdown's `style` is its format, not CSS */}
              <Countdown to={activeRound.deadline} style="clock" suffix=" LEFT" expiredText="TIME'S UP" onExpire={fetchSummary} />
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.2 }}>
              {activeRound.additionalCommitmentCents > 0 ? `${money(activeRound.additionalCommitmentCents, { signed: true })} each` : 'NO EXTRA — AN UPDATE TO ANSWER'}
            </span>
            {activeRound.message && <p style={{ fontSize: 13, lineHeight: 1.6, color: '#D1D5DB', margin: 0 }}>“{activeRound.message}”</p>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: mobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: 6 }} data-testid="mime-tally">
            {[
              { n: activeRound.acceptedCount || 0, k: 'IN', color: GREEN },
              { n: activeRound.declinedCount || 0, k: 'OUT' },
              { n: silent, k: 'SILENT', color: AMBER },
              { n: money(raisedCents), k: 'RAISED', color: GREEN, small: true },
            ].map((c) => (
              <div key={c.k} style={{ background: 'rgba(255,255,255,.08)', borderRadius: 18, padding: '12px 8px', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                <span style={{ fontSize: c.small ? 16 : 20, fontWeight: 700, lineHeight: 1.2, color: c.color || '#fff', overflowWrap: 'anywhere' }}>{c.n}</span>
                <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.1em', color: FAINT }}>{c.k}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <Btn variant="white" full={mobile} onClick={handleNudge} disabled={silent === 0 || nudge.busy || nudge.tooSoon} testId="mime-nudge">{nudgeLabel}</Btn>
            <Btn variant="dark" full={mobile} onClick={handleEndEarly} disabled={ending} testId="mime-end-early">{ending ? 'ENDING…' : 'END THE ROUND EARLY'}</Btn>
          </div>
          <span style={{ fontSize: 11, lineHeight: 1.55, color: FAINT }}>
            {activeRound.removeNonResponders
              ? 'Anyone still silent when the clock runs out is dropped and refunded — you turned "remove non-responders" on.'
              : 'Non-responders keep their ticket when the clock runs out — you left "remove non-responders" off.'}
          </span>
        </Card>
      ) : null}

      {activeRound && (
        <Card gap="12" testId="mime-answers">
          <Head title="WHO ANSWERED" right={<span className="hev-caps-muted">{requests.length} ASKED</span>} />
          {requests.length === 0 ? (
            <Empty bare title="NOBODY WAS ASKED." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {requests.map((req, i) => {
                const row = requestRow(req, activeRound);
                const name = req.guest?.displayName || 'Guest';
                return (
                  <div key={req.id} className="hev-line" data-first={i === 0 ? '' : undefined}>
                    <Ava name={name} imageUrl={req.guest?.avatarUrl} size={36} />
                    <RowText title={name} meta={row.when} bold plainMeta />
                    <StatusPill label={row.pill} />
                    {!mobile && <span className="hev-row-amt" style={{ minWidth: 64 }}>{row.amount}</span>}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      <Card testId="mime-new-round" style={{ gap: 20 }}>
        <SectionHead title={summary?.totalRounds ? 'START ANOTHER ROUND' : 'ASK FOR MORE'} sub="Only when the event genuinely needs it. Two rounds is usually the limit before people tune out." />
        <div className="hev-grid2">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
            <Field label="EXTRA PER GUEST" required htmlFor="mime-amount">
              <AffixInput prefix="$" id="mime-amount" type="number" inputMode="decimal" min="0" max="1000" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" disabled={!!blocked} />
            </Field>
            <Help>$0.00 – $1,000.00</Help>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
            <Field label="SHOT CLOCK" htmlFor="mime-clock">
              <Select id="mime-clock" value={clock} onChange={(e) => setClock(Number(e.target.value))} disabled={!!blocked}>
                {CLOCKS.map(([m, label]) => <option key={m} value={m}>{label}</option>)}
              </Select>
            </Field>
            <Help>5 min – 24 hours</Help>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Field label="WHY YOU'RE ASKING" htmlFor="mime-reason">
            <Textarea id="mime-reason" value={reason} onChange={(e) => setReason(e.target.value.slice(0, 500))} maxLength={500} rows={3} placeholder="Say what the money buys. People say yes to a reason, not a number." style={{ minHeight: 100 }} disabled={!!blocked} />
          </Field>
          <span style={{ fontSize: 11, color: reason.length >= 500 ? '#DC2626' : '#6B7280' }}>{reason.length} of 500 characters</span>
        </div>
        <ToggleRow plain label="Drop anyone who doesn't answer" desc="Off by default. On means silence refunds their ticket and frees the spot." on={dropSilent} onToggle={() => setDropSilent((v) => !v)} disabled={!!blocked} testId="mime-drop-silent" />
        <ErrorLine>{createError}</ErrorLine>
        <Btn size="xl" full onClick={handleCreate} disabled={!!blocked || creating} testId="mime-send-round">
          {blocked || (creating ? 'SENDING…' : `SEND ROUND ${nextRound}`)}
        </Btn>
      </Card>

      {completed.length > 0 && (
        <Card gap="12" testId="mime-completed">
          <Head title="COMPLETED ROUNDS" right={summary?.totalAdditionalCommitmentsCents > 0 ? <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: MONEY_GREEN, whiteSpace: 'nowrap' }}>{money(summary.totalAdditionalCommitmentsCents)} RAISED</span> : null} />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {completed.map((round, i) => (
              <div key={round.id} className="hev-line" data-first={i === 0 ? '' : undefined}>
                <RowText
                  title={`ROUND ${round.roundNumber}`}
                  meta={`${round.acceptedCount}/${round.totalRequests} said yes${round.additionalCommitmentCents > 0 ? ` · ${money(round.additionalCommitmentCents, { signed: true })} each` : ''}${String(round.status).toLowerCase() === 'cancelled' || String(round.status).toLowerCase() === 'canceled' ? ' · ended early' : ''}`}
                  bold
                  plainMeta
                />
                <span className="hev-row-amt" style={{ color: MONEY_GREEN }}>{money((round.acceptedCount || 0) * (round.additionalCommitmentCents || 0))}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {!activeRound && completed.length === 0 && acceptedCount === 0 && (
        <Card center>
          <Empty plain title="NOBODY TO ASK YET.">Accept guests first — a round goes to everyone who is in.</Empty>
        </Card>
      )}
    </div>
  );
}
